<template>
  <v-dialog
    max-width="450px"
    :value="showDialog"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card class="ma-0 pa-0">
      <v-card-title class="py-4 px-6">
        <p class="blue-grey--text text--darken-3 h6 ma-0 pa-0">
          {{ $t('organization_structure.create_site') }}
        </p>
      </v-card-title>
      <v-card-text class="ma-0 py-0 px-6 pt-3">
        <v-form v-model="form.valid" ref="create_site_form">
          <v-text-field
            required
            outlined
            dense
            data-private
            autofocus
            v-model="site.name"
            :maxLength="$constants.site.nameMaxLength"
            :hide-details="Boolean(site.name)"
            :class="Boolean(site.name) ? 'mb-4' : ''"
            :rules="[
              name => !!name || $t('validation.form.this_field_is_required'),
            ]"
            :label="`${$t('common.name')}*`"
          />
          <v-autocomplete
            v-model="site.countryCode"
            data-testid="settings-textField-country"
            dense
            outlined
            required
            :hide-details="Boolean(site.countryCode)"
            :class="Boolean(site.countryCode) ? 'mb-4' : ''"
            :rules="[
              country =>
                !!country || $t('validation.form.this_field_is_required'),
            ]"
            :label="`${$t('common.country_one')}*`"
            :items="countries"
          />
          <v-select
            outlined
            required
            dense
            item-text="name"
            v-model="site.language"
            :hide-details="Boolean(site.language)"
            :class="Boolean(site.language) ? 'mb-4' : ''"
            :rules="[
              language =>
                !!language || $t('validation.form.this_field_is_required'),
            ]"
            :items="availableLanguages"
            :label="`${$t('organization.default_language')}*`"
          />
          <v-select
            outlined
            required
            dense
            v-model="site.currency"
            data-testid="settings-textField-currency"
            :hide-details="Boolean(site.currency)"
            :class="Boolean(site.currency) ? 'mb-4' : ''"
            :rules="[
              currency =>
                !!currency || $t('validation.form.this_field_is_required'),
            ]"
            :label="`${$t('common.currency')}*`"
            :items="currencies"
          />
          <v-autocomplete
            v-model="site.timezone"
            data-testid="settings-textField-timezone"
            dense
            outlined
            required
            :hide-details="Boolean(site.timezone)"
            :class="Boolean(site.timezone) ? 'mb-4' : ''"
            :rules="[
              timezone =>
                !!timezone || $t('validation.form.this_field_is_required'),
            ]"
            :label="`${$t('site_settings.timezone')}*`"
            :items="timezoneOptions"
          />
          <div class="d-flex">
            <label
              for="enable-site"
              class="blue-grey--text text--darken-3 ma-0 pa-0 me-2 body-1"
            >
              {{ $t('organization_structure.site_enabled') }}
            </label>
            <v-switch
              class="ma-0 pa-0"
              id="enable-site"
              hide-details
              v-model="site.isEnabled"
              :false-value="false"
              :input-value="site.isEnabled"
              :ripple="false"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-divider class="mt-6 mb-4" />
      <v-card-actions class="ma-0 pa-0 d-flex justify-end pb-4 px-6">
        <Button
          plain
          color="blueGrey"
          class="me-4"
          :disabled="loading"
          :text="$t('common.button.cancel')"
          @click="closeDialog"
        />
        <Button
          color="primary"
          :loading="loading"
          :disabled="loading"
          :text="$t('common.button.save')"
          @click="handleSave"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import globalCurrencies from '@/locales/currencies.json'
import mixin from '@/mixins/mixin.vue'

import { utils } from 'frontend-js-shared'

import Button from '@/components/UI/Button'

export default {
  name: 'CreateSiteDialog',

  mixins: [mixin],
  props: {
    organization: { type: Object, required: true },
    loading: { type: Boolean, required: true },
    showDialog: { type: Boolean, required: true },
  },
  components: {
    Button,
  },

  data() {
    return {
      form: {
        valid: true,
      },
      site: {
        name: '',
        language: '',
        timezone: '',
        currency: '',
        isEnabled: true,
      },
      timezoneOptions: [],
      globalCurrencies: Object.values(globalCurrencies).map(
        ({ name, code }) => ({
          name,
          value: code,
          text: `${code} - ${name}`,
        })
      ),
      availableLanguages: this.getLanguages(),
    }
  },

  async mounted() {
    this.timezoneOptions = await utils.dates.humanizeTimezones()
  },

  computed: {
    currencies() {
      return this.globalCurrencies.filter(this.isInOrganizationCurrency)
    },

    countries() {
      return this.$constants.countries.map(country => {
        return {
          value: country.code,
          text: this.$t(`countries.${country.code}`),
        }
      })
    },
  },

  methods: {
    handleSave() {
      this.$refs.create_site_form.validate()
      if (!this.form.valid) return
      this.$emit('createSite', this.site)
      this.resetForm()
    },

    resetForm() {
      this.site = {
        name: '',
        language: '',
        timezone: '',
        currency: '',
        isEnabled: true,
      }
      this.$refs?.create_site_form?.reset?.()
    },

    closeDialog() {
      this.resetForm()
      this.$emit('cancel')
    },

    isInOrganizationCurrency(currency) {
      const values = this.organization?.allowedSiteCurrencies?.map(
        c => c.currency
      )
      return values?.includes(currency.value)
    },
  },
}
</script>
